import React from 'react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { notification } from 'antd';
import { NotificationType } from '../models/types';
import { ERROR_MESSAGES, API_STATUS_CODES, NOTIFICATION_TYPES, NOTIFICATION_TITLE } from '../utils/constant';
import Cookies from 'js-cookie';
import i18n from 'i18next';
import { getErrorMessageByStatus } from '../utils/commonFunctions';

const apiClient = axios.create({
    baseURL: "http://157.119.183.2/omdp/public/api",
    //baseURL: "http://4.247.138.229/omdp/public/api",
    //baseURL: "http://localhost/omdp/public/api",   
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor for adding auth tokens, etc.
apiClient.interceptors.request.use(
    (config) => {
        const token = Cookies.get('token');
        const currentLanguage = i18n.language;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers["Accept-Language"] = currentLanguage ?? "en";
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for handling errors globally
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error) => handleApiError(error)
);
let isNotificationDisplayed = false;

//Handle api error response
const handleApiError = (error: any) => {
    if (!axios.isAxiosError(error)) {
        if (!isNotificationDisplayed) {
            const unexpectedErrorMessage = ERROR_MESSAGES.UNEXPECTED_ERROR;
            showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, unexpectedErrorMessage);
            isNotificationDisplayed = true;
        }
        throw new Error(ERROR_MESSAGES.UNEXPECTED_ERROR);
    }

    const customMessage = error?.response?.data?.message;
    if (customMessage) {
        const errorMessage = Array.isArray(customMessage) ? customMessage.join(', ') : customMessage;
        if (!isNotificationDisplayed) {
            // showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, errorMessage);
            isNotificationDisplayed = true;
        }
        throw new Error(`${errorMessage}`);
    }

    if (error.response) {
        const { status, statusText = 'Unknown Error' } = error.response;
        const errorMessage = getErrorMessageByStatus(status) || `${status} - ${statusText}`;
        if (!isNotificationDisplayed) {
            showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, errorMessage);
            isNotificationDisplayed = true;
        }
        throw new Error(errorMessage);
    }

    if (error?.request) {
        const networkErrorMessage = ERROR_MESSAGES.NETWORK_ERROR;
        if (!isNotificationDisplayed) {
            showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, networkErrorMessage);
            isNotificationDisplayed = true;
        }
        throw new Error(networkErrorMessage);
    }

    const axiosErrorMessage = error?.message || ERROR_MESSAGES.UNEXPECTED_ERROR;
    if (!isNotificationDisplayed) {
        showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, axiosErrorMessage);
        isNotificationDisplayed = true;
    }
    throw new Error(axiosErrorMessage);
};

const showNotification = (type: NotificationType, message: string, description: string) => {
    const formattedDescription = (
        <><div className='notError'>
            {description.split(/\r?\n/).map((line, index) => (
                <p key={index}>{line}</p>
            ))}
            </div>
        </>
    );

    notification[type]({
        message: i18n.t(`${message}`),
        description: formattedDescription,
        // duration: 0,
    });
};

//Common method for get api call
export const get = async (url: string, config?: AxiosRequestConfig): Promise<any> => {
    try {
        const response = await apiClient.get(url, config);
        if (response?.status === API_STATUS_CODES.SUCCESS) {
            return response.data;
        }
        const message = response?.data?.message ?? ERROR_MESSAGES.FETCH_ERROR;
        showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, message);
        return response;
    } catch (error) {
        // console.error('GET request error:', error);
        // showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, ERROR_MESSAGES.FETCH_ERROR);
        throw error; // Re-throw if needed by the caller
    }
};

export const post = async (url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<any>> => {
    try {
        const response = await apiClient.post(url, data, { ...config, responseType: config?.responseType || 'json' });

        if (response?.status === API_STATUS_CODES.SUCCESS || response?.status === API_STATUS_CODES.CREATED) {
            return response;
        }

        const message = response?.data?.message ?? ERROR_MESSAGES.POST_ERROR;
        const formattedMessage = formatErrorMessage(message);
        showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, formattedMessage);
        throw new Error(message);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const rawMessage = error.response?.data?.message ?? ERROR_MESSAGES.POST_ERROR;
            const formattedMessage = formatErrorMessage(rawMessage);
            showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, formattedMessage);
            throw new Error(formattedMessage);
        } else {
            const rawMessage = (error as Error).message || ERROR_MESSAGES.POST_ERROR;
            const formattedMessage = formatErrorMessage(rawMessage);
            showNotification(NOTIFICATION_TYPES.ERROR, NOTIFICATION_TITLE.FAILED, formattedMessage);
            throw new Error(formattedMessage);
        }
    }
};

// Formats the error message to handle @@@ and ### markers.
const formatErrorMessage = (rawMessage: string): string => {
    let formattedMessage = '';

    // Split the raw message into parts using @@@ and ###
    const parts = rawMessage.split(/@@@|###/);
    for (let i = 0; i < parts.length; i += 2) {
        const messagePart = parts[i]?.trim();
        const cellsPart = parts[i + 1]?.trim();

        // Add the message and corresponding cells to the formatted message
        if (messagePart) formattedMessage += `${messagePart}\n`;
        if (cellsPart) formattedMessage += `${cellsPart}\n`;
    }

    return formattedMessage.trim(); // Remove trailing whitespace
};


//Common method for put api call
export const put = async (url: string, data?: any, config?: AxiosRequestConfig): Promise<any> => {
    const response = await apiClient.put(url, data, config);
    if (response?.status === API_STATUS_CODES.SUCCESS || response?.status === API_STATUS_CODES.CREATED) {
        return response.data;
    }

    const message = response?.data?.message ?? ERROR_MESSAGES.UPDATE_ERROR;;
    showNotification(NOTIFICATION_TYPES.SUCCESS, NOTIFICATION_TITLE.FAILED, message);
    return response;
};

//Common method for delete api call
export const del = async (url: string, data?: any, config?: AxiosRequestConfig): Promise<any> => {
    const deleteConfig: AxiosRequestConfig = {
        ...config,
        ...(data && { data: data }),
    };
    const response = await apiClient.delete(url, deleteConfig);
    if (response?.status === API_STATUS_CODES.SUCCESS || response?.status === API_STATUS_CODES.ACCEPTED) {
        return response.data;
    }

    const message = response?.data?.message ?? ERROR_MESSAGES.DELETE_ERROR;;
    showNotification(NOTIFICATION_TYPES.SUCCESS, NOTIFICATION_TITLE.FAILED, message);
    return response;
};